
import { PlaceInfo } from './PlaceInfo';
import { PlacePhotos } from './PlacePhotos';
import { PlaceRatings } from './PlaceRatings';
import { PlaceStats } from './PlaceStats';
import { PlaceDetails } from './PlaceDetails';
import { PlaceComments } from './PlaceComments';
import { StructuredData, createRestaurantSchema, createHotelSchema, createLocalBusinessSchema, createFAQSchema } from '@/components/seo/StructuredData';

interface PlaceContentProps {
  id: string;
  place: any;
  editedPlace: any;
  isEditing: boolean;
  isOwner: boolean;
  isLoggedIn: boolean;
  comments: any[];
  onInputChange: (field: string, value: any) => void;
  onCommentSubmit: (content: string) => void;
  onCommentUpdate: (commentId: string, content: string) => void;
  onCommentDelete: (commentId: string) => void;
  isSubmitting: boolean;
}

export function PlaceContent({
  id,
  place,
  editedPlace,
  isEditing,
  isOwner,
  isLoggedIn,
  comments,
  onInputChange,
  onCommentSubmit,
  onCommentUpdate,
  onCommentDelete,
  isSubmitting
}: PlaceContentProps) {
  // Create enhanced structured data based on place type
  const createPlaceSchema = () => {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    
    // Enhanced base data with more properties
    const baseData = {
      name: place.name,
      description: place.note || place.description || `${place.name} located in ${place.city}, ${place.country}`,
      image: place.photos?.[0],
      url: currentUrl,
      address: place.address,
      city: place.city,
      country: place.country,
      latitude: place.latitude,
      longitude: place.longitude,
      rating: place.google_rating,
      reviewCount: place.google_reviews_count,
      priceRange: place.price_range
    };

    switch (place.place_type) {
      case 'restaurant':
        return createRestaurantSchema({
          ...baseData,
          cuisineTypes: place.cuisine_type ? [place.cuisine_type] : undefined,
          // Enhanced restaurant-specific data
          servesCuisine: place.cuisine_type ? [place.cuisine_type] : undefined,
          menu: place.menu_url,
          acceptsReservations: place.accepts_reservations,
          openingHours: place.opening_hours,
          specialDiet: place.special_diet || []
        });
        
      case 'hotel':
        return createHotelSchema({
          ...baseData,
          // Enhanced hotel-specific data
          starRating: place.star_rating,
          amenities: place.amenities,
          roomCount: place.room_count,
          checkinTime: place.checkin_time,
          checkoutTime: place.checkout_time
        });
        
      default:
        // For other place types, use LocalBusiness schema
        return createLocalBusinessSchema(baseData);
    }
  };

  const schema = createPlaceSchema();

  // Create FAQ schema if place has comments that can be structured as Q&A
  const createFAQSchemaFromComments = () => {
    // Only create FAQs if we have at least 2 comments
    if (!comments || comments.length < 2) {
      return null;
    }

    // Construct FAQs from top comments (mock implementation)
    // In a real app, you would identify actual questions in comments
    const faqs = comments
      .filter(comment => comment.content && comment.content.length > 30)
      .slice(0, 5) // Take top 5 substantial comments
      .map(comment => {
        // Generate a question based on the comment
        const fakeQuestion = `What do visitors say about ${place.name}?`;
        
        return {
          question: fakeQuestion,
          answer: comment.content
        };
      });
    
    if (faqs.length > 0) {
      return createFAQSchema(faqs);
    }
    
    return null;
  };

  const faqSchema = createFAQSchemaFromComments();

  // Return both main schema and FAQ schema if available
  const allSchemas = faqSchema ? [schema, faqSchema] : schema;

  return (
    <>
      <StructuredData schema={allSchemas} />
      <div className="max-w-4xl mx-auto space-y-8">
        <PlaceInfo
          isEditing={isEditing && isOwner}
          address={editedPlace.address}
          city={editedPlace.city}
          country={editedPlace.country}
          onAddressChange={(value) => onInputChange('address', value)}
          onCityChange={(value) => onInputChange('city', value)}
          onCountryChange={(value) => onInputChange('country', value)}
        />

        <PlacePhotos
          photos={editedPlace.photos}
          isEditing={isEditing && isOwner}
          onRemovePhoto={(index) => {
            if (!isOwner) return;
            const newPhotos = [...editedPlace.photos];
            newPhotos.splice(index, 1);
            onInputChange('photos', newPhotos);
          }}
        />

        <PlaceRatings
          placeId={id}
          googleRating={place.google_rating}
          googleReviewsCount={place.google_reviews_count}
          isLoggedIn={isLoggedIn}
        />

        <PlaceStats
          place={editedPlace}
          isEditing={isEditing && isOwner}
          onInputChange={onInputChange}
        />

        <PlaceDetails
          place={editedPlace}
          isEditing={isEditing && isOwner}
          onInputChange={onInputChange}
        />

        <PlaceComments
          comments={comments}
          isLoggedIn={isLoggedIn}
          onCommentSubmit={onCommentSubmit}
          onCommentUpdate={onCommentUpdate}
          onCommentDelete={onCommentDelete}
          isSubmitting={isSubmitting}
        />
      </div>
    </>
  );
}
